<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import hljs from 'highlight.js'
import 'highlight.js/styles/atom-one-dark.css'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { marked } from 'marked'

const props = defineProps({
  code: {
    type: [null, String],
    required: true
  },
  language: {
    default: 'plaintext'
  },
  preview: {
    type: Boolean,
    default: false
  },
  references: {
    type: Array,
    default: []
  }
})

   /**
      * Function: removeHighlighted
      * @description This function selects all of the highlighted elements and
      * removes the highlighted data attribute. This avoids a console warning
      * explaining that the code has already been highlighted.
   */
   const removeHighlighted = () => {
      let hls = document.querySelectorAll('.hljs')
      hls.forEach(hl => {
         hl.removeAttribute('data-highlighted')
      })
   }

const renderedMarkdown = ref('')

/**
  * Function: highlightCode
  * @description Initialize the highlight.js library and highlight the code block
*/
const codeBlock = ref(null)

const highlightCode = () => {
  if (codeBlock.value) hljs.highlightElement(codeBlock.value)
}

// On component mount and code change, highlight the code block
onMounted(() => {
  highlightCode()
})

// Watch for code changes and rerun the highlight function
watch(() => props.code, () => {
  highlightCode()
})

// Function to copy code to clipboard
const copied = ref(false)
const copyToClipboard = () => {
  navigator.clipboard.writeText(props.code)
    .then(() => {
      copied.value = true

      setTimeout(() => {
        copied.value = false
      }, 2000)
    })
}

const formattedCode = computed(() => '\n' + props.code)

</script>

<template>
  <div class="cg-code-container" :class="{ 'markdown-notifier': language[0][0] == 'markdown' }">

    <template v-if="! preview">
      <div class="copy-notifier" :class="{ copied: copied }">
        Copied! <FontAwesomeIcon icon="fas fa-check-circle" />
      </div>

      <button class="cg-copy-button" :class="{ 'd-none': ! code }" @click="copyToClipboard()">
        <FontAwesomeIcon icon="fas fa-copy" />
      </button>
    </template>

    <template v-if="language?.[0]?.[0] == 'markdown' && code">

      <div class="cg-markdown" v-html="marked(code)"></div>

      <div v-if="references?.length" class="markdown-references">
        <template v-for="link in references" :key="link">
          <a v-tippy :title="`This is an external link: ${link.url}`" :href="link.url" target="_blank">{{ link.name }} <span class="external-link"><FontAwesomeIcon icon="fas fa-external-link-alt" /></span></a>
        </template>
      </div>
    </template>

    <template v-if="language?.[0]?.[0] != 'markdown' && code">
      <pre>
        <code ref="codeBlock" class="hljs read-only-code" :class="language">
          {{ formattedCode }}
        </code>
      </pre>

      <div v-if="references?.length" class="references">
        <template v-for="link in references" :key="link">
          <a v-tippy :title="`This is an external link: ${link.url}`" :href="link.url" target="_blank">{{ link.name }} <span class="external-link"><FontAwesomeIcon icon="fas fa-external-link-alt" /></span></a>
        </template>
      </div>
    </template>
  </div>
</template>
